<template>
  <qtm-autocomplete
    v-bind="$attrs"
    v-model="contactProxy"
    clearable
    data-test="contact-select"
    hide-details="auto"
    :items="items"
    placeholder="Search Existing Contacts"
    return-object
  >
    <template v-slot:item="{ item, props: itemProps}">
      <v-list-item
        v-bind="itemProps"
        :data-test="item?.raw?.internalDistribution ? 'internal-distribution' : undefined"
      >
        <template v-slot:title>
          {{ item?.raw?.text }}
          <out-of-office-status v-if="item?.raw?.outOfOffice" class="ml-2" />
        </template>
      </v-list-item>
    </template>
    <template v-for="(_, name) in $slots" v-slot:[name]="slotData" :key="name">
      <slot v-bind="slotData" :key="name" :name="name" />
    </template>
  </qtm-autocomplete>
</template>

<script setup lang="ts">
import OutOfOfficeStatus from '@/components/vendors/out-of-office-status.vue'

interface Contact {
  id: number | string
  first_name?: string
  email?: string
  internalDistribution?: boolean
  last_name?: string
  outOfOffice?: boolean
  text?: string
}

export interface Props {
  allowInternal?: boolean
  contact?: Contact | null
  contacts?: Contact[]
  internalDistribution?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  allowInternal: false,
  contact: null,
  contacts: () => [],
  internalDistribution: false,
})
const emit = defineEmits(['update:contact', 'update:internal-distribution'])

const internalDistributionItem: Contact = {
  id: 'internal-distribution',
  text: 'Internal PO Distribution Only',
  internalDistribution: true,
}

const contactProxy = computed<Contact | null>({
  get() {
    if (props.internalDistribution) {
      return internalDistributionItem
    }

    if (props.contact) {
      return {
        ...props.contact,
        text: `${props.contact.first_name} ${props.contact.last_name} (${props.contact.email})`
      }
    }

    return null
  },
  set(value: Contact | null) {
    if (value?.internalDistribution) {
      emit('update:contact', null)
      emit('update:internal-distribution', true)
    }
    else {
      emit('update:contact', value)
      emit('update:internal-distribution', false)
    }
  },
})
const items = computed<Contact[]>(() => {
  const itemsWithText = props.contacts.map((c) => ({
    ...c,
    text: `${c.first_name} ${c.last_name} (${c.email})`,
    outOfOffice: !!c.ooo_date_started
  }))

  if (props.allowInternal) {
    return itemsWithText.concat([internalDistributionItem as any])
  }

  return itemsWithText
})
</script>
